// Shared medal data for the gamification surfaces — bands, labels, section
// help text and the tier-history parser. Every number a user sees comes from
// the live summary endpoints (useLearnerGamification / useAdminGamification).
//
// The Employer Scorecard and Employee Readiness Score concept blocks that
// used to live here were hardcoded worked examples ("Acme Corp 72/Gold",
// "Jamie Chen 71/Gold"); they and their two components are gone, along with
// every other demo value in this folder (RDP, Sep 2026 — placeholder data
// reached real learners once the dashboard went GA). Never reintroduce
// placeholder numbers here.

// ── Learner — Medals (Cian feedback v2, replaces the Readiness framing) ─────
// Five sections, each a banded progress bar (Rust/Bronze/Silver/Gold) with a
// marker at the learner's position. Overall medal = the LOWEST medal held
// across all sections (open decision: lowest vs average vs excluding s5).
// Platinum: top 10% of members drawn only from the pool already at Gold in
// that section; no Gold pool → no Platinum (protects new orgs).
// Band boundaries assumed as 0–25 / >25–50 / >50–75 / >75–100 (open decision).

import { isCapabilityRelease } from '../../utils/capabilityRelease';

export type MedalKey = 'wood' | 'bronze' | 'silver' | 'gold' | 'platinum';

export interface Medal {
  key: MedalKey;
  label: string;
  color: string;
}

// Saturated tier colours — the earlier muted set read as flat grey-brown on
// the dark surface. Rendered with a highlight gradient (see MedalScore) so the
// bands and badges catch the light like metal rather than sitting flat.
// The tiers' names. With the Capability release they are Activity levels:
// Low, Moderate, High, Excellent and (learners only) Exceptional; without it
// (utils/capabilityRelease.ts) the metal names stay. Keys and colours are the
// same either way, so a label is read when it is shown, never stored.
const TIER_NAMES: Record<MedalKey, { activity: string; metal: string }> = {
  wood: { activity: 'Low', metal: 'Rust' },
  bronze: { activity: 'Moderate', metal: 'Bronze' },
  silver: { activity: 'High', metal: 'Silver' },
  gold: { activity: 'Excellent', metal: 'Gold' },
  platinum: { activity: 'Exceptional', metal: 'Platinum' }
};

export const tierName = (key: MedalKey): string => TIER_NAMES[key][isCapabilityRelease() ? 'activity' : 'metal'];

const medal = (key: MedalKey, color: string): Medal => ({
  key,
  color,
  get label() {
    return tierName(key);
  }
});

export const MEDALS: Record<MedalKey, Medal> = {
  // Internal key stays 'wood' (BE payload + persisted values use it).
  wood: medal('wood', '#8F6136'),
  bronze: medal('bronze', '#DE7F35'),
  silver: medal('silver', '#C3CDD9'),
  gold: medal('gold', '#DFA50D'),
  platinum: medal('platinum', '#3E9BE0')
};

// Where the Platinum band starts on every bar. Platinum itself is relative
// (top 10% of the Gold pool), so the bar shows it as the final stretch of the
// track rather than a score threshold.
export const PLATINUM_START = 90;

// What the admin Activity tab calls each measure. The BE's names stay the keys
// (help copy, rankings, payloads); only the label on screen changes.
const MEASURE_LABELS: Record<string, string> = {
  'Learning Gaps Closed': 'Capability Gaps Closed',
  'Learning Range': 'Training & Assessments Started',
  'Proactive Learning': 'Questions to Elliott',
  'Practice Rounds': 'Practice Roleplays'
};
export const measureLabel = (name: string): string => MEASURE_LABELS[name] ?? name;

/** Measures shown as their own score (a count or recency) on the Activity tab, not as a %. */
export const SCORE_AS_VALUE = new Set(['Proactive Learning', 'Roleplays', 'Practice Rounds']);

export interface MedalSection {
  name: string;
  measure: string;
  /** Marker position on the bar, 0–100. */
  pos: number;
  /** Display value ("68%" or "5 messages"). */
  value: string;
  /**
   * Null when nothing this section measures is required of the learner
   * (Audience Requirements, DA-5056 R5: a learner who only reads every lesson
   * has no Knowledge Retention or Mastery). The BE sends value "-" and pct
   * null with it; the section shows "-", no medal and no marker, and is left
   * out of the overall tier. Never null for a learner with required work.
   */
  medal: MedalKey | null;
  /** Band boundary positions on the bar, 0–100 (ticks between the 4 tiers). */
  boundaries: [number, number, number];
  /**
   * Per-band copy for a section whose track isn't a percentage. Proactive
   * Learning measures recency, so its position is a band slot rather than a
   * score and "0–24%" would be meaningless. Indexed like the tier keys: wood,
   * bronze, silver, gold, platinum. Absent means the axis is a real percentage.
   */
  bandLabels?: string[];
  /** Boundary-tick labels, in the same case — indexed along the gates. */
  gateLabels?: string[];
  /** Raw count behind a count-based section (messages), for hover copy. */
  count?: number;
}

/**
 * The bracket a band covers on a section's bar, in the section's own units:
 * "0–24%" on percentage bars, "3–6 messages" on count bars (axisMax set). The
 * last band on the track is open-ended.
 */
export const bandRange = (section: MedalSection, from: number, to: number, isLast: boolean, bandIndex = -1): string => {
  const label = section.bandLabels?.[bandIndex];
  if (label) return label;
  return isLast ? `${Math.round(from)}–100%` : `${Math.round(from)}–${Math.round(to) - 1}%`;
};

const PCT_BOUNDARIES: [number, number, number] = [25, 50, 75];

/** One weekly snapshot of the overall tier position on the 0–100 tier track. */
export interface TierHistoryPoint {
  /** ISO date (yyyy-mm-dd) of the week the snapshot covers. */
  weekStart: string;
  /** Overall position 0–100, banded exactly like the measure bars. */
  score: number;
}

/** Weekly history straight off the summary payload; anything malformed is dropped. */
export const parseTierHistory = (raw: unknown): TierHistoryPoint[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p: any) => ({
      weekStart: typeof p?.weekStart === 'string' ? p.weekStart : typeof p?.date === 'string' ? p.date : '',
      score: typeof p?.score === 'number' ? p.score : NaN
    }))
    .filter((p) => p.weekStart !== '' && Number.isFinite(p.score))
    .map((p) => ({ weekStart: p.weekStart, score: Math.max(0, Math.min(100, p.score)) }))
    .sort((a, b) => a.weekStart.localeCompare(b.weekStart));
};

/**
 * Which medal a percentage earns on a standard percentage bar — the same
 * 25/50/75 bands every other percentage section uses, with Platinum from
 * PLATINUM_START. Sections adapted from the BE carry their own `medal`;
 * anything computed at runtime (Learning Gaps Closed) derives it from here so
 * the two can never drift apart.
 */
export const medalForPct = (pct: number): MedalKey => {
  if (pct >= PLATINUM_START) return 'platinum';
  if (pct >= PCT_BOUNDARIES[2]) return 'gold';
  if (pct >= PCT_BOUNDARIES[1]) return 'silver';
  if (pct >= PCT_BOUNDARIES[0]) return 'bronze';
  return 'wood';
};

/**
 * The Learning Gaps Closed section, built from live counts rather than stored
 * dummy data. Sits at the TOP of the admin scorecard: it is the one measure an
 * admin can move directly, today, by acting on what Elliott has surfaced —
 * everything else on the card moves only when learners do something.
 *
 * With no gaps found at all the section reads 100%: nothing outstanding is a
 * clean sheet, not a zero.
 */
export const buildGapsClosedSection = (closed: number, total: number): MedalSection & { medal: MedalKey } => {
  const pct = total === 0 ? 100 : Math.round((closed / total) * 100);
  return {
    name: 'Learning Gaps Closed',
    measure: isCapabilityRelease()
      ? 'The % of identified capability gaps you have closed'
      : 'The % of identified knowledge gaps you have closed',
    pos: pct,
    value: `${pct}%`,
    medal: medalForPct(pct),
    boundaries: PCT_BOUNDARIES
  };
};

// What each measure means and how to move it — surfaced behind the "How To
// Improve" tooltip on every section row, keyed by the section name the BE
// sends (and the dummy data mirrors).
export interface SectionHelp {
  definition: string;
  howToImprove: string;
}

// Admins are told what to encourage across their learners; learners are given
// the instruction directly, in the second person.
export const SECTION_HELP: Record<string, { admin: SectionHelp; learner: SectionHelp }> = {
  // Worded for capability gaps with the Capability release, for knowledge gaps without it.
  'Learning Gaps Closed': {
    admin: {
      get definition() {
        return isCapabilityRelease()
          ? 'This measures the % of the capability gaps I have identified that you have since closed. A capability gap counts as closed once every one of my recommended actions on it is marked closed or ignored.'
          : 'This measures the % of the knowledge gaps I have identified that you have since closed. A gap counts as closed once every recommendation on it is marked done or ignored.';
      },
      get howToImprove() {
        return isCapabilityRelease()
          ? 'Open the Capability tab and work through Capability Measures. On each capability, mark my recommended actions as closed or ignored. Updating the lesson, discussing it with the employee, publishing the recommended course and adding the knowledge I asked for all count; so does ignoring an action you disagree with.'
          : 'Work through the recommendations on each gap in Learning Gaps. Publishing the recommended course, adding the knowledge I asked for, or updating the lesson all count; so does ignoring a recommendation you disagree with.';
      }
    },
    learner: {
      get definition() {
        return isCapabilityRelease()
          ? 'This measures the % of identified capability gaps that have been closed.'
          : 'This measures the % of identified knowledge gaps that have been closed.';
      },
      howToImprove: 'Your program admin closes these — keep completing lessons and asking questions.'
    }
  },
  'Lesson Completion': {
    admin: {
      definition: 'This measures the % of lessons completed across all programs and learners.',
      howToImprove: 'Encourage learners to complete more lessons across their programs.'
    },
    learner: {
      definition: 'This measures the % of lessons you have completed across your programs.',
      howToImprove: 'Complete more lessons across your programs.'
    }
  },
  'Knowledge Retention': {
    admin: {
      definition: "This measures learners' scores across all program quiz assessments.",
      howToImprove:
        'Encourage learners to use Improve Performance to retake failed quiz assessments. It appears on the program card of any program where a quiz was failed.'
    },
    learner: {
      definition: 'This measures your scores across all program quiz assessments.',
      howToImprove:
        'Use Improve Performance to retake failed quiz assessments. It appears on the program card of any program where you failed a quiz.'
    }
  },
  'Knowledge Mastery': {
    admin: {
      definition:
        "This measures learners' pass rate across all live scenario trainings with Elliott, retakes included.",
      howToImprove:
        'Encourage learners to use Improve Performance to retake failed live scenarios. It appears on the program card of any program where a live scenario was failed.'
    },
    learner: {
      definition: 'This measures your pass rate across all live scenario trainings with Elliott, retakes included.',
      howToImprove:
        'Use Improve Performance to retake failed live scenarios. It appears on the program card of any program where you failed a live scenario.'
    }
  },
  'Learning Range': {
    admin: {
      definition: "This measures the % of a learner's enrolled programs that they have actually begun.",
      howToImprove: 'Encourage learners to begin programs they have not yet started.'
    },
    learner: {
      definition: 'This measures the % of your enrolled programs that you have actually begun.',
      howToImprove: 'Begin programs you have not yet started.'
    }
  },
  'Proactive Learning': {
    admin: {
      definition:
        'This measures the % of your learners who have asked Elliott a question in Conversations, In-App or in MS Teams, in the last 30 days.',
      howToImprove:
        'Encourage learners to ask Elliott questions in MS Teams or In-App. This measures how many of your learners are asking questions at all, not how many questions they ask, so it rises fastest when learners who have never used Elliott start.'
    },
    learner: {
      definition: 'This measures how recently you last asked Elliott a question in Conversations.',
      get howToImprove() {
        return isCapabilityRelease()
          ? 'Ask Elliott a question in MS Teams or In-App. You reach Excellent by asking a question in the last 7 days (High within 14 days, Moderate within 30). Exceptional is awarded if you’ve sent 3 or more messages to Elliott in the last 7 days.'
          : 'Ask Elliott a question in MS Teams or In-App. You reach Gold by asking a question in the last 7 days (Silver within 14 days, Bronze within 30). Unlike in other measures, Platinum for Proactive Learning is awarded if you’ve sent 3 or more messages to Elliott in the last 7 days.';
      }
    }
  },
  // Roleplays and Practice Rounds are scored like Proactive Learning: recency
  // bands for a learner (7 / 14 / 30 days, Exceptional at 3+ in 7 days), and
  // for an admin the % of their learners with at least one in the last 30 days.
  Roleplays: {
    admin: {
      definition:
        'This measures the % of your employees who have completed at least one Roleplay in the last 30 days. Roleplays in lessons and graded Roleplays they enter themselves both count, and so do retakes.',
      howToImprove:
        'Encourage employees to complete the Roleplay in each lesson, and to enter a graded Roleplay on any capability they want to improve. This measures how many of your employees are doing Roleplays at all, not how many each one does, so it rises fastest when employees who have not done one recently start.'
    },
    learner: {
      definition:
        'This measures how recently you last completed a Roleplay, in a lesson or a graded Roleplay you entered yourself. Retakes count.',
      howToImprove:
        'Complete the Roleplay in a lesson, or enter a graded Roleplay on any capability you want to improve. You reach Excellent by completing one in the last 7 days (High within 14 days, Moderate within 30). Exceptional is awarded if you’ve completed 3 or more in the last 7 days.'
    }
  },
  'Practice Rounds': {
    admin: {
      definition:
        'This measures the % of your employees who have run at least one practice round in the last 30 days. A practice round is a practice Roleplay before a lesson or a graded Roleplay. It is never graded, and every run counts.',
      howToImprove:
        'Encourage employees to run a practice round before each lesson Roleplay or graded Roleplay. This measures how many of your employees are practising at all, not how many rounds each one runs, so it rises fastest when employees who have not practised recently start.'
    },
    learner: {
      definition:
        'This measures how recently you last ran a practice round: a practice Roleplay before a lesson or a graded Roleplay. It is never graded, and every run counts.',
      howToImprove:
        'Run a practice round before your next lesson Roleplay or graded Roleplay. You reach Excellent by running one in the last 7 days (High within 14 days, Moderate within 30). Exceptional is awarded if you’ve run 3 or more in the last 7 days.'
    }
  }
};

// The learner instructions as an admin reads them on one member's tier (Org
// Members): about the member, in the third person. Keyed like SECTION_HELP.
export const MEMBER_VIEW_HOW_TO_IMPROVE: Record<string, (memberName: string) => string> = {
  'Lesson Completion': (memberName) => `${memberName} must complete more lessons across their programs.`,
  'Knowledge Retention': (memberName) =>
    `${memberName} must use Improve Performance to retake failed quiz assessments. It appears on the program card of any program where they failed a quiz.`,
  'Knowledge Mastery': (memberName) =>
    `${memberName} must use Improve Performance to retake failed live scenarios. It appears on the program card of any program where they failed a live scenario.`,
  'Learning Range': (memberName) => `${memberName} must begin the programs they have not yet started.`,
  'Proactive Learning': (memberName) => `${memberName} must ask Elliott questions in MS Teams or In-App.`,
  Roleplays: (memberName) =>
    `${memberName} must complete the Roleplay in a lesson, or enter a graded Roleplay on a capability they want to improve.`,
  'Practice Rounds': (memberName) =>
    `${memberName} must run a practice round before a lesson Roleplay or graded Roleplay.`
};

// Predicted outcome profile per tier (Bersin HILO · LinkedIn Workplace
// Learning · ATD), extended down to Rust for the org-level tier panel.
export interface TierOutcomeRow {
  metric: string;
  wood: string;
  bronze: string;
  silver: string;
  gold: string;
  platinum: string;
}

export const TIER_OUTCOMES: TierOutcomeRow[] = [
  { metric: 'Annual churn', wood: '35%+', bronze: '25–35%', silver: '18–22%', gold: '12–17%', platinum: '<12%' },
  { metric: 'Retention rate', wood: '<25%', bronze: '~27%', silver: '~40–45%', gold: '~55–65%', platinum: '~65–75%' },
  { metric: 'Productivity lift', wood: 'None', bronze: 'None', silver: '+0–12%', gold: '+12–24%', platinum: '+24%+' },
  {
    metric: 'Peer cohort',
    wood: 'Bottom 25%',
    bronze: 'Bottom 50%',
    silver: 'Top 50%',
    gold: 'Top 20%',
    platinum: 'Top 6%'
  }
];

// The five medal sections are rendered from LIVE data only — the learner and
// admin summary endpoints (useLearnerGamification / useAdminGamification).
// The old hardcoded medal placeholders were removed after their numbers
// shipped to prod and were reported as wrong org data (RDP, Sep 2026).
// Never reintroduce placeholder numbers here.

/**
 * How a measure's value reads in the narrow value column.
 *
 * Only Proactive Learning needs translating. The backend sends its value as
 * recency prose — "Today", "Yesterday", "3 days ago", or "None in 30 days"
 * when the learner has never messaged Elliott. Every other row in this panel
 * shows a figure, and the prose both overflowed a column sized for "68%" and
 * read as a sentence, so all four now render as "+Xd": days since the learner
 * last reached Elliott, or the whole window when they never have.
 *
 * The "+" is literal for the never-messaged case, where the true gap is at
 * least that window and possibly much longer. For the other three it is the
 * exact number of days; they carry the "+" so the column reads as one measure
 * rather than two formats. The tooltip states the real meaning in each case,
 * which is why it is not one sentence with the number swapped in: "no messages
 * in the last 0 days" would be nonsense for someone who wrote today.
 *
 * Every other measure passes through untouched and gets no tooltip.
 */
// What each recency measure counts the days since, as its hover says it.
const RECENCY_SUBJECT: Record<string, string> = {
  'Proactive Learning': 'question to Elliott',
  Roleplays: 'Roleplay',
  'Practice Rounds': 'Practice Round'
};

/**
 * A recency value as "+Xd", with "X days since your last <subject>." on hover.
 * "None in 30 days" is "+30d": at least that long, and possibly never. Anything
 * else the backend sends (an admin's "42%", new copy) is shown as sent.
 */
const recencyValue = (raw: string, subject: string): { text: string; tooltip: string } => {
  const never = /^None in (\d+) days?$/i.exec(raw);
  if (never) return { text: `+${never[1]}d`, tooltip: `More than ${never[1]} days since your last ${subject}.` };
  if (/^today$/i.test(raw)) return { text: '+0d', tooltip: `Your last ${subject} was today.` };
  if (/^yesterday$/i.test(raw)) return { text: '+1d', tooltip: `1 day since your last ${subject}.` };
  const ago = /^(\d+) days? ago$/i.exec(raw);
  if (ago) {
    const days = Number(ago[1]);
    return { text: `+${days}d`, tooltip: `${days} ${days === 1 ? 'day' : 'days'} since your last ${subject}.` };
  }
  return { text: raw, tooltip: '' };
};

export function formatSectionValue(sectionName: string, value: string): { text: string; tooltip: string } {
  const raw = (value || '').trim();
  // The Capability release: the three recency measures share one format and one way of explaining it.
  if (isCapabilityRelease() && RECENCY_SUBJECT[sectionName]) return recencyValue(raw, RECENCY_SUBJECT[sectionName]);
  if (sectionName !== 'Proactive Learning') return { text: raw, tooltip: '' };

  const never = /^None in (\d+) days?$/i.exec(raw);
  if (never) {
    const days = never[1];
    return {
      text: `+${days}d`,
      tooltip: `No messages sent to Elliott in the last ${days} days.`
    };
  }

  if (/^today$/i.test(raw)) {
    return { text: '+0d', tooltip: 'Last message sent to Elliott today.' };
  }

  if (/^yesterday$/i.test(raw)) {
    return { text: '+1d', tooltip: 'No messages sent to Elliott since yesterday.' };
  }

  const ago = /^(\d+) days? ago$/i.exec(raw);
  if (ago) {
    const days = ago[1];
    return {
      text: `+${days}d`,
      tooltip: `No messages sent to Elliott in the last ${days} days.`
    };
  }

  // An unrecognised shape (new copy on the backend) is shown as sent.
  return { text: raw, tooltip: '' };
}
